<template>
  <SDataTable
    :table="table"
    :show-page-size-select="true"
    :page-size-options="[10, 25, 50, 100]"
  />
</template>

<script setup lang="ts">
import { h } from "vue";
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/vue-table";
import { SDataTable } from "@smeetz/ui-components";
import { useI18n } from "vue-i18n";

import { isProductAvailable } from "@/utils/vue-helpers/product-category";
import { sumArray } from "@/utils/helpers/common";
import { PCTypes, RetailOptionType } from "@/types/db/product-category";
import type { IProductTableData } from "@/types/db/product-category";

import { useProductsContext } from "./useProductsContext";

import ProductImageCell from "./products-cells/ProductImageCell/ProductImageCell.vue";
import ProductNameCell from "./products-cells/ProductNameCell/ProductNameCell.vue";
import ProductDescriptionCell from "./products-cells/ProductDescriptionCell/ProductDescriptionCell.vue";
import ProductSelectButtonCell from "./products-cells/ProductSelectButtonCell/ProductSelectButtonCell.vue";

type Props = {
  products: IProductTableData[];
};

const props = defineProps<Props>();
const { t } = useI18n({});
const ctx = useProductsContext();

/**
 * Retail-stock arithmetic, copy of the legacy `countAvailability` helper.
 * Lives here so the availability sortingFn (in column defs) can call it.
 *
 * @param {IProductTableData} product - Row data.
 * @returns {number}
 */
function countAvailability(product: IProductTableData): number {
  if (product.type !== PCTypes[PCTypes.Retail]) {
    return 0;
  }

  if (product.prices.length === 1) {
    return product.prices[0].quantity;
  }

  return sumArray(
    product.prices
      .filter((pr) =>
        pr.retail.every(
          (re) => re.type !== RetailOptionType.Default_Retail_Option
        )
      )
      .map((pr) => pr.quantity)
  );
}

const columnHelper = createColumnHelper<IProductTableData>();

const columns = [
  columnHelper.display({
    id: "image",
    header: () => t("products.image"),
    enableSorting: false,
    size: 130,
    minSize: 130,
    cell: ({ row }) => h(ProductImageCell, { row: row.original }),
  }),

  columnHelper.accessor(
    (row) => ctx.getTranslationForProducts(row.nameLocale) || row.name,
    {
      id: "name",
      header: () => t("products.name"),
      enableSorting: true,
      sortingFn: "alphanumeric",
      size: 240,
      minSize: 100,
      cell: ({ row }) => h(ProductNameCell, { row: row.original }),
    }
  ),

  columnHelper.display({
    id: "description",
    header: () => t("products.description"),
    enableSorting: false,
    size: 320,
    minSize: 100,
    cell: ({ row }) => h(ProductDescriptionCell, { row: row.original }),
  }),

  columnHelper.accessor(
    (row) => {
      if (row.isRecurring) {
        return -1;
      }

      return isProductAvailable(row) || countAvailability(row);
    },
    {
      id: "availability",
      header: () => t("field.availability"),
      enableSorting: true,
      size: 160,
      minSize: 100,
      cell: ({ row, getValue }) => {
        if (row.original.isRecurring) {
          return "";
        }

        return h("p", { class: "text-center" }, [
          h("span", {}, String(getValue())),
          ` ${t("pcselect.left")}`,
        ]);
      },
    }
  ),

  columnHelper.accessor((row) => row.prices?.[0]?.value ?? 0, {
    id: "price",
    header: () => t("products.price"),
    enableSorting: true,
    size: 160,
    minSize: 100,
    cell: ({ row }) => {
      const p = row.original;
      const formatted = (p.prices[0].value / 100).toFixed(2);

      return h("p", { class: "text-center word-alignment" }, [
        h("span", {}, t("common.from-semicolon")),
        ` ${p.product.currency} ${formatted}`,
      ]);
    },
  }),

  columnHelper.display({
    id: "select",
    header: () => t("cells.actions"),
    enableSorting: false,
    size: 220,
    minSize: 220,
    cell: ({ row }) => h(ProductSelectButtonCell, { row: row.original }),
  }),
];

const table = useVueTable({
  get data() {
    return props.products;
  },
  columns,
  enableColumnResizing: true,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: { pageSize: 10, pageIndex: 0 },
  },
});
</script>
